/**
 * <td-story-slider> — owns video playback for sections/td-story-slider.liquid.
 *
 * Wraps a <swiper-slider> and drives: active-slide-only playback, a progress
 * bar, a shared mute toggle, tap-to-pause, and pausing everything when the
 * section scrolls out of view.
 *
 * Swiper is lazy-loaded by assets/app.js on intersection, so this component has
 * to work both before and after Swiper boots. Active-slide tracking therefore
 * uses whichever source is available, in order: the Swiper instance's
 * activeIndex, the `swiper-slide-active` class, then the first slide.
 *
 * It marks the active slide itself with `data-active` (and the host with
 * `data-ready`) rather than letting the CSS key off `swiper-slide-active`, so
 * presentation never depends on Swiper's class bookkeeping — and if this script
 * fails to run, no slide is dimmed instead of all of them.
 *
 * NB: in loop mode Swiper 11 physically reorders slides in the DOM, so the
 * MutationObserver must watch childList as well as class attributes.
 *
 * Deliberately NOT using the `video-slider` attribute on the inner
 * <swiper-slider>: that activates bindVideoSliderIntegration() in
 * assets/swiper-slider.js, which would double-bind slide-change playback and
 * click-to-toggle against this component.
 */
if (!customElements.get('td-story-slider')) {
  class TdStorySlider extends HTMLElement {
    constructor() {
      super();
      this.activeSlide = null;
      this.activeVideo = null;
      this.inViewport = false;
      this.userPaused = false;
      this.muted = true;
      this.rafId = null;
      this.swiperBound = false;
      // Last values published to the slider column; see publishCardWidth.
      this.cardW = null;
      this.cardH = null;
      this.onTimeUpdate = this.handleTimeUpdate.bind(this);
      this.onPlaybackChange = this.syncPausedState.bind(this);
      this.pointerStart = null;
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onSync = this.syncActiveSlide.bind(this);
    }

    connectedCallback() {
      this.wrapper = this.querySelector('.swiper-wrapper');
      this.sliderEl = this.querySelector('swiper-slider');
      if (!this.wrapper) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayEnabled = this.dataset.autoplay !== 'false' && !this.reducedMotion;
      this.progressEnabled = this.dataset.progress !== 'false';

      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('click', this.onClick);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.applyMuted(true);
      this.watchActiveSlide();
      this.watchCardWidth();
      this.syncActiveSlide();
      this.watchViewport();
    }

    disconnectedCallback() {
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.pointerStart = null;
      if (this.slideObserver) this.slideObserver.disconnect();
      if (this.viewportObserver) this.viewportObserver.disconnect();
      if (this.cardObserver) this.cardObserver.disconnect();
      if (this.swiper && typeof this.swiper.off === 'function') {
        this.swiper.off('slideChange', this.onSync);
        this.swiper.off('slideChangeTransitionEnd', this.onSync);
      }
      this.swiperBound = false;
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = null;
      this.releaseActiveSlide();
      this.pauseAll();
      this.activeSlide = null;
      this.activeVideo = null;
    }

    /* --------------------------------------------------- active slide */

    // Watches both the slide classes and childList: loop mode reorders slides in
    // the DOM, which is a childList mutation, and class updates can land while a
    // slide is detached (and so go unreported). Debounced to one sync per frame.
    watchActiveSlide() {
      this.slideObserver = new MutationObserver(() => {
        if (this.rafId) return;
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          this.syncActiveSlide();
        });
      });
      this.slideObserver.observe(this.wrapper, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    // Swiper's own events are the most direct signal, but the instance only
    // exists after the lazy-loaded bundle initialises it, so bind on first sight.
    bindSwiperEvents() {
      if (this.swiperBound) return;
      const swiper = this.sliderEl && this.sliderEl.slider;
      if (!swiper || typeof swiper.on !== 'function') return;
      this.swiperBound = true;
      this.swiper = swiper;
      swiper.on('slideChange', this.onSync);
      swiper.on('slideChangeTransitionEnd', this.onSync);
      // The MutationObserver only existed to notice the lazy-loaded instance
      // appearing (swiper-slider.js emits no DOM event on init). Swiper's events
      // are authoritative from here, so stop the duplicate DOM watching.
      if (this.slideObserver) {
        this.slideObserver.disconnect();
        this.slideObserver = null;
      }
    }

    // Publishes the rendered card width so the desktop arrow rail can be centred
    // on the card's real edges. Swiper writes slide widths inline and the card is
    // additionally capped by the max-height setting, so this can't be derived in
    // CSS alone. Observed rather than computed once, since the width changes on
    // resize and when Swiper re-runs its breakpoints.
    watchCardWidth() {
      this.cardObserver = new ResizeObserver(() => this.publishCardWidth());
      this.cardObserver.observe(this.wrapper);
      this.publishCardWidth();
    }

    publishCardWidth() {
      const card = (this.activeSlide || this.wrapper).querySelector('.td-ss__card');
      if (!card) return;
      const box = card.getBoundingClientRect();
      const width = Math.round(box.width);
      const height = Math.round(box.height);
      if (width < 16) return;
      // Write straight onto the arrow rail, the only consumer. It must not go on
      // this element or the slider column: both are ancestors of the swiper, and
      // Swiper's observer would treat the style write as a DOM change. Only write
      // on a real change regardless, since this runs from a ResizeObserver.
      const col = this.closest('.td-ss__slider-col');
      const scope = col && col.querySelector('.td-ss__arrows');
      if (!scope) return;
      if (this.cardW !== width) {
        this.cardW = width;
        scope.style.setProperty('--td-ss-card-w', `${width}px`);
      }
      // Height lets the rail centre on the card rather than on the column, which
      // is taller by however much the pagination row below adds.
      if (this.cardH !== height) {
        this.cardH = height;
        scope.style.setProperty('--td-ss-card-h', `${height}px`);
      }
    }

    resolveActiveSlide() {
      const swiper = this.sliderEl && this.sliderEl.slider;
      if (swiper && swiper.slides && swiper.slides[swiper.activeIndex]) {
        return swiper.slides[swiper.activeIndex];
      }
      return (
        this.wrapper.querySelector('.swiper-slide-active') ||
        this.wrapper.querySelector('.td-ss__slide')
      );
    }

    syncActiveSlide() {
      this.bindSwiperEvents();

      const next = this.resolveActiveSlide();
      if (!next || next === this.activeSlide) return;

      this.releaseActiveSlide();

      this.activeSlide = next;
      this.activeVideo = next.querySelector('video');
      this.userPaused = false;
      next.removeAttribute('data-paused');
      next.setAttribute('data-active', '');
      // Idempotent: this element is a parent of the swiper, which Swiper watches
      // for attribute changes once observeParents is on.
      if (!this.hasAttribute('data-ready')) this.setAttribute('data-ready', '');
      this.publishCardWidth();

      if (this.activeVideo) {
        this.activeVideo.muted = this.muted;
        if (this.progressEnabled) {
          this.activeVideo.addEventListener('timeupdate', this.onTimeUpdate);
        }
        // The overlay follows the video's real state, not just user intent, so a
        // slide that never starts (autoplay off, reduced motion, or a blocked
        // play()) still offers a play affordance.
        this.activeVideo.addEventListener('play', this.onPlaybackChange);
        this.activeVideo.addEventListener('pause', this.onPlaybackChange);
      }

      if (this.inViewport) {
        this.play();
      } else if (!this.autoplayEnabled) {
        // Settle the overlay before the slide scrolls into view, so it does not
        // pop in. Only safe when nothing is going to auto-start.
        this.syncPausedState();
      }
    }

    releaseActiveSlide() {
      if (this.activeVideo) {
        this.activeVideo.removeEventListener('timeupdate', this.onTimeUpdate);
        this.activeVideo.removeEventListener('play', this.onPlaybackChange);
        this.activeVideo.removeEventListener('pause', this.onPlaybackChange);
        this.activeVideo.pause();
        try {
          this.activeVideo.currentTime = 0;
        } catch {
          // Seeking can throw before metadata is available; harmless.
        }
      }
      if (this.activeSlide) {
        this.activeSlide.removeAttribute('data-paused');
        this.activeSlide.removeAttribute('data-active');
        this.activeSlide.style.removeProperty('--td-ss-progress');
      }
    }

    /* ------------------------------------------------------- playback */

    // Mirrors the active video's paused state onto the slide, which is what
    // surfaces the play overlay. Driven by the video's own play/pause events so
    // it can't drift from reality.
    syncPausedState() {
      if (!this.activeSlide || !this.activeVideo) return;
      if (this.activeVideo.paused) {
        this.activeSlide.setAttribute('data-paused', '');
      } else {
        this.activeSlide.removeAttribute('data-paused');
      }
    }

    play() {
      if (!this.activeVideo) return;
      if (!this.autoplayEnabled || this.userPaused) {
        // Nothing is going to start it, so show the play affordance now.
        this.syncPausedState();
        return;
      }
      const played = this.activeVideo.play();
      if (played && typeof played.catch === 'function') {
        played.catch(() => {
          // Autoplay can still be blocked (e.g. unmuted). No pause event fires
          // for an already-paused video, so surface the overlay by hand.
          this.syncPausedState();
        });
      }
    }

    pauseAll() {
      this.querySelectorAll('video').forEach((video) => video.pause());
    }

    // If Swiper initialised while the container had no width — inside a tab,
    // drawer, or anything display:none at init — updateSize() bails and leaves
    // the instance unmeasured, with no slide classes and no breakpoint applied.
    // Becoming visible is precisely when to re-measure. This replaces Swiper's
    // observeParents, which covered the same case but fired on every ancestor
    // attribute write and so reordered slides during unrelated interactions.
    recoverSwiperSize() {
      const swiper = this.sliderEl && this.sliderEl.slider;
      if (!swiper || swiper.destroyed || swiper.size) return;
      swiper.update();
    }

    watchViewport() {
      this.viewportObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.inViewport = entry.isIntersecting;
            if (entry.isIntersecting) {
              this.recoverSwiperSize();
              this.play();
            } else {
              this.pauseAll();
            }
          });
        },
        { threshold: 0.25 }
      );
      this.viewportObserver.observe(this);
    }

    /* ------------------------------------------------------- progress */

    handleTimeUpdate() {
      if (!this.activeVideo || !this.activeSlide) return;
      const duration = this.activeVideo.duration;
      if (!duration || !isFinite(duration)) return;
      const progress = Math.min(this.activeVideo.currentTime / duration, 1);
      this.activeSlide.style.setProperty('--td-ss-progress', progress.toFixed(4));
    }

    /* ----------------------------------------------------- interaction */

    handlePointerDown(event) {
      this.pointerStart = { x: event.clientX, y: event.clientY };
    }

    // How far the pointer may travel and still count as a tap. Swiper's own
    // touch threshold is 5px; allow a little more slack than that.
    static get TAP_SLOP() {
      return 10;
    }

    handleClick(event) {
      // A swipe Swiper does not consume still arrives here as a click. Left
      // unfiltered it pauses the video instead of moving the carousel, which is
      // what a missed swipe feels like. detail === 0 means a keyboard-generated
      // click, which carries no meaningful coordinates.
      if (event.detail !== 0 && this.pointerStart) {
        const moved = Math.max(
          Math.abs(event.clientX - this.pointerStart.x),
          Math.abs(event.clientY - this.pointerStart.y)
        );
        this.pointerStart = null;
        if (moved > TdStorySlider.TAP_SLOP) return;
      }

      const muteButton = event.target.closest('[data-td-mute]');
      if (muteButton) {
        event.preventDefault();
        event.stopPropagation();
        this.applyMuted(!this.muted);
        return;
      }

      const card = event.target.closest('.td-ss__card');
      if (!card) return;

      const slide = card.closest('.td-ss__slide');
      if (!slide || slide !== this.activeSlide || !this.activeVideo) return;

      // Only the intent flag is set here; the video's play/pause events own the
      // data-paused attribute, so a rejected play() can't leave the overlay lying.
      if (this.activeVideo.paused) {
        this.userPaused = false;
        const played = this.activeVideo.play();
        if (played && typeof played.catch === 'function') {
          played.catch(() => this.syncPausedState());
        }
      } else {
        this.userPaused = true;
        this.activeVideo.pause();
      }
    }

    // The muted state lives on each button's aria-pressed, which the CSS keys off
    // for the struck-through icon. It deliberately does NOT go on this element:
    // that is an ancestor of the swiper, and writing to it on every toggle made
    // Swiper re-run loopFix() and reorder the slides mid-click.
    applyMuted(muted) {
      this.muted = muted;
      this.querySelectorAll('video').forEach((video) => {
        video.muted = muted;
      });
      this.querySelectorAll('[data-td-mute]').forEach((button) => {
        button.setAttribute('aria-pressed', muted ? 'true' : 'false');
        button.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      });
    }

    /* ---------------------------------------------------- theme editor */

    handleBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const index = parseInt(event.target.dataset.slideIndex, 10);
      if (Number.isNaN(index)) return;

      const swiper = this.sliderEl && this.sliderEl.slider;
      if (swiper && swiper.params && swiper.params.loop && typeof swiper.slideToLoop === 'function') {
        swiper.slideToLoop(index);
      } else if (this.sliderEl && typeof this.sliderEl.goToSlide === 'function') {
        this.sliderEl.goToSlide(index);
      }
    }
  }

  customElements.define('td-story-slider', TdStorySlider);
}
